import {Component, EventEmitter, OnInit, Output, ViewChild} from '@angular/core';
import {combineLatest, merge, Observable, of} from "rxjs";
import {Cart} from "../../../webshop/cart/state/model";
import {CartService} from "../../../webshop/cart/state/service";
import {AuthService} from "../../../authentication/service/auth.service";
import {User} from "../../../authentication/user/state/model";
import {FormBuilder, FormControl} from "@angular/forms";
import {InvoiceAddressService} from "../../../webshop/invoice-address/state/service";
import {filter, map, shareReplay, switchMap, take, tap} from "rxjs/operators";
import {InvoiceAddress} from "../../../webshop/invoice-address/state/model";
import {FormService} from "../../../components/service/form.service";
import {EditAddressComponent} from "../../editAddress";
import {Address} from "../../../components/entity";
import {Country} from "../../../webshop/country/state/model";

@Component({
  selector: 'app-checkout-billing-address',
  templateUrl: './billing-address.html'
})
export class BillingAddressComponent implements OnInit {

  @ViewChild(EditAddressComponent) editAddress: EditAddressComponent;

  copyControl: FormControl;
  cart$: Observable<Cart>;
  user$: Observable<User> = this.authService.user$;
  addresses$: Observable<InvoiceAddress[]>;
  addressControl: FormControl;
  newAddress: InvoiceAddress = new InvoiceAddress({});
  @Output() next = new EventEmitter();

  constructor(
    private cartService: CartService,
    private authService: AuthService,
    private addressService: InvoiceAddressService,
    private fb: FormBuilder,
    public formService: FormService
  ) {
    this.copyControl = fb.control(true);
    this.addressControl = fb.control(new InvoiceAddress({}), this.formService.bindValidations([this.formService.getValidations(['required'])]));
    this.cart$ = this.cartService.cart$;
    this.addresses$ = combineLatest([
      this.user$.pipe(filter(user => !!user.id)),
      merge(of(undefined), this.addressService.addressListChanged$),
    ]).pipe(
      switchMap(([user]) => this.addressService.getByUser(user)),
      shareReplay(1)
    );
  }

  ngOnInit(): void {
    combineLatest([
      this.addresses$,
      this.cartService.cart$.pipe(take(1)),
    ]).pipe(
      take(1),
      tap(([addresses, cart]) => this.preselectAddress(addresses, cart))
    ).subscribe();
  }

  private preselectAddress(addresses: InvoiceAddress[], cart: Cart): void {
    const matched = this.findMatchingAddress(addresses, cart);
    if (matched) {
      this.addressControl.setValue(matched);
      return;
    }

    const _def = addresses.find(address => address.isDefault);
    if (_def) {
      this.addressControl.setValue(_def);
    }
  }

  private findMatchingAddress(addresses: InvoiceAddress[], cart: Cart): InvoiceAddress | undefined {
    if (cart.billingAddress) {
      const billingMatch = addresses.find(address =>
        this.matchAddress(address, cart.billingAddress, cart.billingCountry)
      );
      if (billingMatch) {
        return billingMatch;
      }
    }
    if (cart.shippingAddress) {
      return addresses.find(address =>
        this.matchAddress(address, cart.shippingAddress, cart.shippingCountry)
      );
    }
    return undefined;
  }

  private matchAddress(addr: InvoiceAddress, embedded: Address, country: Country): boolean {
    return addr.country.id === country.id &&
      addr.address.city === embedded.city &&
      addr.address.street === embedded.street;
  }

  save(cart: Cart) {
    if (this.addressControl.value.id) {
      cart.billingAddress = this.addressControl.value.address;
      cart.billingCountry = this.addressControl.value.country;
      this.cartService.update(cart.id, cart)
        .pipe(
          tap(() => this.next.emit())
        )
        .subscribe();
    } else {
      if (this.editAddress.getAddress()) {
        this.user$.pipe(
          switchMap(user => {
            const address = this.editAddress.getAddress();
            address.user = user;
            return this.addressService.add(address).pipe(
              switchMap((_address: InvoiceAddress) => {
                cart.billingAddress = _address.address;
                cart.billingCountry = _address.country;
                this.addressService.notifyAddressesChanged(_address);
                return this.cartService.update(cart.id, cart)
                  .pipe(
                    tap(() => this.next.emit())
                  )
              })
            )
          })
        ).subscribe();
      }
    }
  }

}
